import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "471",
  "slug": "what-is-a-karst-landscape",
  "title": "What Is a Karst Landscape? Caves, Sinkholes, and Underground Rivers",
  "excerpt": "Some of the world's strangest scenery, from limestone towers to rivers that vanish underground, is the work of slightly acidic water. Welcome to karst.",
  "date": "2026-06-17",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1770637112710-40b2e3c2d333?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxrYXJzdCUyMGxpbWVzdG9uZSUyMG1vdW50YWluc3xlbnwxfDB8fHwxNzgxNjg0MjI5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A karst landscape forms where slightly acidic water slowly dissolves soluble bedrock like limestone, creating caves, sinkholes, disappearing streams, and dramatic rock formations. Famous examples include Guilin in China and the limestone country of Slovenia, where the word karst comes from.",
  "content": [
    {
      "type": "paragraph",
      "text": "Picture rivers that disappear into the ground, hills shaped like green sugarloaves, and entire cave systems hidden beneath your feet. These are the hallmarks of karst, one of the most distinctive and otherworldly types of landscape on Earth. And remarkably, all of it is sculpted by nothing more than water that is very slightly acidic."
    },
    {
      "type": "heading",
      "text": "What Is Karst?"
    },
    {
      "type": "paragraph",
      "text": "Karst is a type of landscape that forms when soluble rock, most often limestone, is slowly dissolved by water over long periods of time. The result is terrain full of caves, sinkholes, underground streams, and dramatic surface formations. The word itself comes from the Kras region of Slovenia and Italy, a classic example that gave the whole phenomenon its name. Wherever the right kind of rock and enough water come together, the same strange features appear, from southern China to the American Midwest."
    },
    {
      "type": "heading",
      "text": "How Karst Forms"
    },
    {
      "type": "paragraph",
      "text": "The secret ingredient is mildly acidic water. As rainwater falls and seeps through soil, it absorbs carbon dioxide and becomes a weak carbonic acid. That acid is strong enough, given enough time, to dissolve limestone along its cracks and joints. Drop by drop, the water widens those fractures into channels, then passages, then full cave systems. The process is slow almost beyond comprehension, but over thousands and millions of years it can hollow out entire mountains from the inside."
    },
    {
      "type": "heading",
      "text": "The Features Karst Creates"
    },
    {
      "type": "paragraph",
      "text": "Karst country comes with its own vocabulary of strange landforms:"
    },
    {
      "type": "list",
      "items": [
        "Caves, the underground galleries carved as water dissolves rock from within.",
        "Sinkholes, also called dolines, where the surface collapses into a hollow below.",
        "Disappearing streams, rivers that vanish down a hole and flow on underground.",
        "Karst towers and pinnacles, steep limestone hills left standing as the rock around them dissolves.",
        "Springs, where the underground water finally re-emerges at the surface.",
        "Stalactites and stalagmites, the mineral formations that grow inside the caves over centuries."
      ]
    },
    {
      "type": "heading",
      "text": "Famous Karst Landscapes"
    },
    {
      "type": "list",
      "items": [
        "Guilin and Yangshuo in China, where misty limestone towers rise above the Li River.",
        "Ha Long Bay in Vietnam, a drowned karst landscape of thousands of jagged islands.",
        "The Dinaric karst of Slovenia and Croatia, the region that gave karst its name.",
        "The Yucatán Peninsula in Mexico, dotted with water-filled sinkholes called cenotes.",
        "Carlsbad Caverns and Mammoth Cave in the United States, among the great cave systems of the world."
      ]
    },
    {
      "type": "heading",
      "text": "Why Karst Matters"
    },
    {
      "type": "paragraph",
      "text": "Karst is more than scenery. A large share of the world's people rely on water stored in karst aquifers, and because that water moves quickly through open passages rather than filtering slowly through soil, karst regions can be especially vulnerable to pollution. Sinkholes can also open suddenly beneath roads and buildings, which makes understanding karst important for engineers and city planners as well as geographers."
    },
    {
      "type": "heading",
      "text": "Karst and Caving"
    },
    {
      "type": "paragraph",
      "text": "Karst is also the reason caving exists as an adventure at all. The same dissolving process that drains rivers underground carves out the vast cave systems that explorers map kilometer by kilometer. Some of these systems run for hundreds of kilometers and reach astonishing depths, and many have still never been fully explored, hidden in the dark beneath ordinary-looking hills."
    },
    {
      "type": "heading",
      "text": "Life on Karst"
    },
    {
      "type": "paragraph",
      "text": "People have always made the most of karst, even when it makes life difficult. The water-filled cenotes of the Yucatán were sacred wells to the ancient Maya and remain a vital water source today. Around the Mediterranean and across southern China, fertile pockets of soil between limestone towers have been farmed for centuries. The catch is that water drains away fast through karst, so communities often depend on springs and underground rivers rather than surface streams, and a polluted sinkhole upstream can quickly taint a spring far away."
    },
    {
      "type": "heading",
      "text": "Spotting Karst from Above"
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, karst landscapes are unmistakable once you know the look: clusters of round depressions pockmarking the ground, dense fields of conical or tower-like hills, and lakes and rivers that seem to start and stop for no reason. The drowned towers of Ha Long Bay and the green pinnacles around Guilin are especially striking from orbit. Want to find some for yourself? Explore a few rounds of EarthGuessr and keep an eye out for that tell-tale pockmarked terrain."
    }
  ]
};
